import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, AppBar, Toolbar, IconButton, Typography, Chip, Button, Divider,
  List, ListItem, ListItemIcon, ListItemText, Checkbox, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Stack,
  Accordion, AccordionSummary, AccordionDetails, InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaceIcon from '@mui/icons-material/Place';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { tasksAPI } from '../../services/api';
import { STATUS_LABELS, STATUS_COLORS } from './taskConstants';
import resizeImage from './resizeImage';
import TaskPhoto from './TaskPhoto';

/**
 * Boton que abre un selector de imagen.
 *
 * `capture` es lo que distingue los dos caminos: con el, el movil abre la camara
 * directamente; sin el, abre el selector del telefono (galeria, Google Fotos,
 * Archivos...). En el navegador de escritorio los dos acaban en el explorador de
 * ficheros, pero en el movil hacen falta los dos botones, porque `capture` no
 * deja elegir una foto ya hecha.
 */
function PhotoInputButton({ label, icon, capture, onChange, disabled, uploading, ...props }) {
  return (
    <Button component="label" disabled={disabled} sx={{ height: 48 }} {...props}
      startIcon={uploading ? <CircularProgress size={18} /> : icon}>
      {label}
      <input hidden type="file" accept="image/*"
        {...(capture ? { capture: 'environment' } : {})}
        onChange={onChange} />
    </Button>
  );
}

/**
 * Detalle de la tarea: toda la informacion para ejecutarla de forma autonoma y
 * las acciones fijas abajo, siempre a un toque.
 */
export default function TaskDetail({ taskId, onBack }) {
  const [task, setTask] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // 'blocked' | 'done'
  const [comment, setComment] = useState('');
  const [hours, setHours] = useState('');
  const [photoPath, setPhotoPath] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [deletePhotoOf, setDeletePhotoOf] = useState(null);  // parte cuya foto se va a borrar
  const [deletingPhoto, setDeletingPhoto] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await tasksAPI.get(taskId);
      setTask(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido cargar la tarea');
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status, extra = {}) => {
    setBusy(true);
    try {
      const res = await tasksAPI.setStatus(taskId, { status, ...extra });
      setTask(res.data);
      setDialog(null);
      setComment(''); setHours(''); setPhotoPath(''); setPhotoPreview(null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido actualizar');
    } finally {
      setBusy(false);
    }
  };

  // Se sube al momento de hacerla: al pulsar Finalizar la foto ya esta en el
  // servidor y el cierre no depende de que aguante la cobertura.
  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const resized = await resizeImage(file);
      const formData = new FormData();
      formData.append('file', resized, resized.name || 'foto.jpg');
      const res = await tasksAPI.uploadPhoto(taskId, formData);
      setPhotoPath(res.data.photo_path);
      setPhotoPreview(URL.createObjectURL(resized));
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido subir la foto');
    } finally {
      setUploading(false);
      e.target.value = '';   // permite repetir la misma foto
    }
  };

  // Foto suelta, sin tener que cerrar la tarea: es lo que se hace en obra a
  // media faena para dejar constancia de como va el montaje.
  const handleQuickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const resized = await resizeImage(file);
      const formData = new FormData();
      formData.append('file', resized, resized.name || 'foto.jpg');
      const up = await tasksAPI.uploadPhoto(taskId, formData);
      const res = await tasksAPI.addUpdate(taskId, {
        photo_path: up.data.photo_path,
        comment: 'Foto de obra',
      });
      setTask(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido subir la foto');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const toggleItem = async (itemId) => {
    try {
      const res = await tasksAPI.toggleChecklistItem(itemId);
      setTask(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido actualizar el checklist');
    }
  };

  // Comentario suelto en el historico, sin foto ni cambio de estado: dejar
  // constancia de algo mientras se esta ejecutando la tarea. Se guarda solo
  // con salir del campo (blur), sin pedir una confirmacion aparte.
  const saveNote = async () => {
    const text = noteText.trim();
    if (!text || noteBusy) return;
    setNoteBusy(true);
    setError('');
    try {
      const res = await tasksAPI.addUpdate(taskId, { comment: text });
      setTask(res.data);
      setNoteText('');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido guardar el comentario');
    } finally {
      setNoteBusy(false);
    }
  };

  // Quitar una foto colgada por error. El backend decide si borra solo la foto
  // o el parte entero (cuando la foto era lo unico que tenia).
  const deletePhoto = async () => {
    if (!deletePhotoOf) return;
    setDeletingPhoto(true);
    setError('');
    try {
      const res = await tasksAPI.deleteUpdatePhoto(taskId, deletePhotoOf.id);
      setTask(res.data);
      setDeletePhotoOf(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido borrar la foto');
    } finally {
      setDeletingPhoto(false);
    }
  };

  if (!task) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const isOpen = !['done', 'verified', 'cancelled'].includes(task.status);
  const leader = task.team_leader_contact;

  const infoBlock = (icon, title, text) => text ? (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.3 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
      </Box>
    </Box>
  ) : null;

  return (
    <Box sx={{ pb: 16 }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onBack} aria-label="Volver"><ArrowBackIcon /></IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={600} noWrap>{task.code}</Typography>
          </Box>
          <Chip size="small" label={STATUS_LABELS[task.status] || task.status}
            color={STATUS_COLORS[task.status] || 'default'} />
        </Toolbar>
      </AppBar>

      {error && <Alert severity="error" sx={{ m: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>{task.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {task.manufacturing_order_code || 'Sin orden'}
          {task.project_name ? ` · ${task.project_name}` : ''}
        </Typography>

        {task.status === 'blocked' && task.blocked_reason && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>{task.blocked_reason}</Alert>
        )}

        {(task.priority && task.priority !== 'normal') || task.requires_photo ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
            {task.priority && task.priority !== 'normal' && (
              <Chip size="small" label={`Prioridad ${task.priority}`}
                color={['alta', 'urgente'].includes(task.priority) ? 'error' : 'default'} />
            )}
            {task.requires_photo && (
              <Chip size="small" variant="outlined" icon={<PhotoCameraIcon />}
                label="Necesita foto para cerrarla" />
            )}
          </Stack>
        ) : null}

        <Divider sx={{ my: 2 }} />

        {/* Indicaciones: lo que la oficina ha escrito al crear la tarea. Va antes
            que nada, porque es lo que hay que leer para hacerla bien. */}
        <Typography variant="overline" color="text.secondary">Indicaciones</Typography>

        {task.safety_notes && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 0.5, mb: 1.5 }}>
            <Typography variant="caption" display="block" fontWeight={600}>Seguridad</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{task.safety_notes}</Typography>
          </Alert>
        )}

        {task.description ? (
          <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
            {task.description}
          </Typography>
        ) : (
          !task.safety_notes && !task.required_tools && !task.required_materials && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Esta tarea no lleva indicaciones. Si te falta informacion, llama al jefe de equipo.
            </Typography>
          )
        )}

        {infoBlock(<PlaceIcon fontSize="small" />, 'Ubicacion', task.location)}
        {infoBlock(<BuildIcon fontSize="small" />, 'Herramientas', task.required_tools)}
        {infoBlock(<Inventory2Icon fontSize="small" />, 'Materiales', task.required_materials)}

        <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Previsto</Typography>
            <Typography variant="body2">{task.estimated_hours ?? '-'} h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Imputado</Typography>
            <Typography variant="body2">{task.actual_hours ?? 0} h</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Fechas</Typography>
            <Typography variant="body2">
              {task.planned_start || '-'}{task.planned_end ? ` a ${task.planned_end}` : ''}
            </Typography>
          </Box>
        </Box>

        {/* Checklist: marcar puntos da el progreso sin pedir porcentajes */}
        {task.checklist?.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Checklist ({task.checklist.filter((i) => i.is_done).length}/{task.checklist.length})
            </Typography>
            <List dense disablePadding>
              {task.checklist.map((item) => (
                <ListItem key={item.id} disablePadding
                  onClick={() => isOpen && toggleItem(item.id)}
                  sx={{ cursor: isOpen ? 'pointer' : 'default', py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    <Checkbox edge="start" checked={!!item.is_done} disabled={!isOpen}
                      tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{
                    sx: { textDecoration: item.is_done ? 'line-through' : 'none',
                          color: item.is_done ? 'text.disabled' : 'text.primary' },
                  }} />
                </ListItem>
              ))}
            </List>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Foto o comentario en cualquier momento, no solo al cerrar la tarea. Para
            la foto hay dos caminos: la camara al vuelo, o una imagen que ya este
            en el telefono. */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <PhotoInputButton fullWidth variant="outlined" capture
            disabled={uploading} uploading={uploading}
            icon={<PhotoCameraIcon />}
            label={uploading ? 'Subiendo...' : 'Hacer foto'}
            onChange={handleQuickPhoto} />
          <PhotoInputButton fullWidth variant="outlined"
            disabled={uploading}
            icon={<PhotoLibraryIcon />}
            label="Elegir foto"
            onChange={handleQuickPhoto} />
        </Stack>
        {/* Comentario: campo siempre a la vista, igual que las Indicaciones. Se
            guarda solo al salir del campo, sin pedir un "Guardar" aparte. */}
        <Typography variant="overline" color="text.secondary">Comentario</Typography>
        <TextField fullWidth multiline minRows={2} placeholder="Anota como va, una incidencia..."
          value={noteText} onChange={(e) => setNoteText(e.target.value)}
          onBlur={() => noteText.trim() && saveNote()}
          disabled={noteBusy}
          helperText="Se guarda en historico al salir del campo"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {noteBusy ? <CircularProgress size={16} /> : <ChatBubbleOutlineIcon fontSize="small" color="disabled" />}
              </InputAdornment>
            ),
          }}
          sx={{ mt: 0.5, mb: leader?.phone ? 1 : 0.5 }} />

        {leader?.phone && (
          <Button fullWidth variant="outlined" startIcon={<PhoneIcon />}
            href={`tel:${leader.phone}`} sx={{ height: 48 }}>
            Llamar a {leader.name}
          </Button>
        )}

        {task.updates?.length > 0 && (
          <Accordion elevation={0} sx={{ mt: 2, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <HistoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="subtitle2">Historico ({task.updates.length})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <Stack spacing={1}>
                {task.updates.map((u) => (
                  <Box key={u.id} sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {u.created_at?.slice(0, 16).replace('T', ' ')} · {u.author || 'Sistema'}
                    </Typography>
                    <Typography variant="body2">
                      {u.status_to ? `${STATUS_LABELS[u.status_to] || u.status_to}` : ''}
                      {u.hours ? ` · ${u.hours} h` : ''}
                    </Typography>
                    {u.comment && <Typography variant="body2">{u.comment}</Typography>}
                    {u.photo_path && (
                      <Box sx={{ mt: 0.5, maxWidth: 200, position: 'relative', display: 'inline-block' }}>
                        <TaskPhoto filename={u.photo_path} driveLink={u.drive_web_link} />
                        {u.can_delete_photo && (
                          <IconButton size="small" aria-label="Borrar foto"
                            onClick={() => setDeletePhotoOf(u)}
                            sx={{
                              position: 'absolute', top: 2, right: 2,
                              backgroundColor: 'rgba(0,0,0,0.55)', color: '#fff',
                              '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                            }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>

      {/* Acciones fijas abajo: siempre visibles, siempre a un toque */}
      {isOpen && (
        <Box sx={{
          position: 'fixed', bottom: 56, left: 0, right: 0, p: 1.5,
          backgroundColor: 'background.paper', borderTop: '1px solid',
          borderColor: 'divider', display: 'flex', gap: 1,
        }}>
          {task.status !== 'in_progress' ? (
            <Button fullWidth variant="contained" startIcon={<PlayArrowIcon />}
              disabled={busy} onClick={() => setStatus('in_progress')}
              sx={{ height: 52, fontSize: 16 }}>
              Empezar
            </Button>
          ) : (
            <>
              <Button variant="outlined" startIcon={<PauseIcon />} disabled={busy}
                onClick={() => setStatus('assigned')} sx={{ height: 52, flex: 1 }}>
                Pausar
              </Button>
              <Button variant="outlined" color="error" startIcon={<BlockIcon />}
                disabled={busy} onClick={() => setDialog('blocked')}
                sx={{ height: 52, flex: 1 }}>
                Bloqueo
              </Button>
            </>
          )}
          <Button variant="contained" color="success" startIcon={<CheckIcon />}
            disabled={busy} onClick={() => setDialog('done')}
            sx={{ height: 52, flex: 1 }}>
            Finalizar
          </Button>
        </Box>
      )}

      {/* Motivo del bloqueo */}
      <Dialog open={dialog === 'blocked'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Que impide seguir?</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline rows={3} label="Motivo" value={comment}
            onChange={(e) => setComment(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="error" disabled={!comment.trim() || busy}
            onClick={() => setStatus('blocked', { comment })}>
            Marcar bloqueo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cierre: horas y foto si la tarea la exige */}
      <Dialog open={dialog === 'done'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Finalizar tarea</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Horas dedicadas" value={hours}
            onChange={(e) => setHours(e.target.value.replace(',', '.'))}
            inputProps={{ inputMode: 'decimal' }} sx={{ mt: 1, mb: 2 }} />
          {task.requires_photo && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>Esta tarea necesita una foto.</Alert>
              {photoPreview && (
                <Box component="img" src={photoPreview} alt="Foto"
                  sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', mb: 1,
                        border: '1px solid', borderColor: 'divider', borderRadius: 1 }} />
              )}
              <Stack direction="row" spacing={1}>
                <PhotoInputButton fullWidth capture
                  variant={photoPath ? 'outlined' : 'contained'}
                  color={photoPath ? 'success' : 'primary'}
                  disabled={uploading} uploading={uploading}
                  icon={<PhotoCameraIcon />}
                  label={uploading ? 'Subiendo...' : photoPath ? 'Repetir' : 'Hacer foto'}
                  onChange={handlePhoto} />
                <PhotoInputButton fullWidth variant="outlined"
                  color={photoPath ? 'success' : 'primary'}
                  disabled={uploading}
                  icon={<PhotoLibraryIcon />}
                  label="Elegir foto"
                  onChange={handlePhoto} />
              </Stack>
            </>
          )}
          <TextField fullWidth multiline rows={2} label="Comentario (opcional)" value={comment}
            onChange={(e) => setComment(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="success" disabled={busy}
            onClick={() => setStatus('done', {
              hours: hours || undefined,
              comment: comment || undefined,
              photo_path: photoPath || undefined,
            })}>
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>


      {/* Borrar una foto del historico: siempre con confirmacion */}
      <Dialog open={!!deletePhotoOf} onClose={() => setDeletePhotoOf(null)} fullWidth maxWidth="xs">
        <DialogTitle>Borrar foto</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres borrar esta foto? No se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePhotoOf(null)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={deletingPhoto} onClick={deletePhoto}>
            Borrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
